<template>
  <OrganizationUnavailable v-if="isOrganizationUnavailable" />
  <div v-else class="flex min-h-screen flex-col">
    <div class="flex items-center justify-center flex-1 mt-8">
      <StateContainer
        :variant="StateVariant.ERROR"
        :illustration="
          is404 ? IllustrationVariant.HTTP_404 : IllustrationVariant.HTTP_500
        "
        :header="is404 ? '404 Error' : '500 Error'"
        :subheader="
          is404
            ? 'Page not found. Refresh and try again.'
            : 'Internal server error. Please try again later.'
        "
        primary-cta-label="Go to Dashboard"
        @primary-click="handleError"
      />
    </div>
    <Footer v-if="!isInIframe" class="mt-8" />
  </div>
</template>

<script setup lang="ts">
import { useIsInIframe } from '~/composables/use-is-in-iframe';
import { Footer } from '~/components/footer';
import OrganizationUnavailable from './organization-unavailable.vue';
import { ORGANIZATION_UNAVAILABLE_STATUS } from '~/api';
import { clearError, computed, useRoute, watch } from '#imports';
import { useOrganizationStore } from '~/store/organization';
import { resolveDefaultLandingUrl } from '~/composables/use-default-landing-url';
import {
  StateContainer,
  StateVariant,
  IllustrationVariant,
} from '~/components/state-container';

type Props = {
  error: Record<string, unknown>;
};
const props = defineProps<Props>();
const isInIframe = useIsInIframe();

const is404 = computed(() => props.error.statusCode === 404);
const isOrganizationUnavailable = computed(
  () => props.error.statusCode === ORGANIZATION_UNAVAILABLE_STATUS,
);

const { organization } = storeToRefs(useOrganizationStore());
const route = useRoute();

const IFRAME_404_REDIRECT_GUARD_KEY = 'iframe-404-redirected-from';

const handleError = () =>
  clearError({ redirect: resolveDefaultLandingUrl(organization.value) });

watch(
  isInIframe,
  (inIframe) => {
    if (!inIframe || !is404.value) {
      return;
    }
    try {
      if (
        sessionStorage.getItem(IFRAME_404_REDIRECT_GUARD_KEY) === route.fullPath
      ) {
        return;
      }
      sessionStorage.setItem(IFRAME_404_REDIRECT_GUARD_KEY, route.fullPath);
    } catch {
      return;
    }
    handleError();
  },
  { immediate: true },
);
</script>
